import { Injectable } from "@angular/core";
import { JobPharmaDetailService } from "../../services";
import { Entity, Job, Material, SampleGroup, TaskCohort, TaskInstance } from "@common/types";
import { isAnyTaskEndState } from "src/app/tasks/util/is-any-task-end-state";
import { DataManagerService } from "@services/data-manager.service";
import { uniqueArrayOnProperty } from "@common/util";
import { WorkflowService } from "src/app/workflow/services/workflow.service";
import { FeatureFlagService } from "@services/feature-flags.service";
import { createLookup } from "@common/util/create-lookup";

@Injectable()
export class JobPharmaTasksListTableService {
    readonly LOG_TAG = 'job-pharma-tasks-list-table-service';

    constructor(
        private jobPharmaDetailService: JobPharmaDetailService,
        private dataManager: DataManagerService,
        private workflowService: WorkflowService,
        private featureFlagService: FeatureFlagService
    ) { }

    async removeCohortFromTask(job: Job, taskCohort: TaskCohort) {
        const sampleGroupsWithSamples = await this.jobPharmaDetailService
            .getSampleGroupsWithSamples([taskCohort.TaskInstance]);
        const materialsWithSamples = this.findMaterialsThatHaveSamplesCreatedFrom(taskCohort);
        if (materialsWithSamples.length && this.jobPharmaDetailService.isSampleGroupsEditableFlag()) {
            const deletedSamples = await this.jobPharmaDetailService.handleSampleAssociationDeletes(
                sampleGroupsWithSamples, 
                materialsWithSamples, 
                true
            );
            if (!deletedSamples) {
                return;
            }
        } else if (materialsWithSamples.length > 0  && this.isTaskLocked(taskCohort.TaskInstance)) {
            return this.jobPharmaDetailService.showNotifyMaterialsWithDataModal(materialsWithSamples)
        }

        return this.removeCohortsFromTasks(job, [taskCohort]);
    }

    private async removeCohortsFromTasks(job: Job, taskCohorts: TaskCohort[]) {
        // TODO: Remove expands which can be loaded in on initial job load
        await this.dataManager.lazyLoadExpands(taskCohorts as Entity<TaskCohort>[], [
            'TaskInstance.MemberTaskInstance.TaskJob',
            'TaskInstance.MemberTaskInstance.TaskOutputSet.TaskOutput.TaskOutputSetMaterial',
            'TaskInstance.MemberTaskInstance.TaskOutputSet.TaskOutput.TaskOutput',
            'TaskInstance.MemberTaskInstance.TaskMaterial',
            'TaskInstance.TaskCohort.TaskCohortInput'
        ]);

        const taskCohortsWithData: TaskCohort[] = [];
        for (const taskCohort of taskCohorts.slice()) {
            // Create lookup to see if materials in cohort are in other task cohorts assigned to same group task
            const lookup = this.createTaskCohortMaterialLookup(taskCohort);
            if (this.findMaterialsWithData(taskCohort, lookup).length > 0) {
                taskCohortsWithData.push(taskCohort);
                continue;
            }
            
            await this.removeTaskCohortAssociations(job, taskCohort, lookup);
        }

        if (taskCohortsWithData.length > 0) {
            await this.jobPharmaDetailService.notifyCohortsWithData(
                uniqueArrayOnProperty(taskCohortsWithData.map(tc => tc.Cohort), "C_Cohort_key")
            );
        }

        this.jobPharmaDetailService.tabRefresh("tasks", "list");
    }

    private async removeTaskCohortAssociations(job: Job, taskCohort: TaskCohort, lookup: Map<number, TaskCohort[]>) {
        const groupTaskInstance = taskCohort.TaskInstance;
        const sampleGroupSourceMaterials = groupTaskInstance.SampleGroup
            .flatMap((sg: SampleGroup) => sg.SampleGroupSourceMaterial);
        for (const sourceMaterial of sampleGroupSourceMaterials) {
            if (!lookup.has(sourceMaterial.C_Material_key) || lookup.get(sourceMaterial.C_Material_key).length > 1) {
                continue;
            }

            this.dataManager.deleteEntity(sourceMaterial);
        }

        const cohort = taskCohort.Cohort;
        const memberTaskInstances = groupTaskInstance.MemberTaskInstance;
        const taskMaterials = memberTaskInstances
            .flatMap((mti: TaskInstance) => mti.TaskMaterial);
        for (const taskMaterial of taskMaterials) {
            if (!lookup.has(taskMaterial.C_Material_key) || lookup.get(taskMaterial.C_Material_key).length > 1) {
                continue;
            }

            const taskInstance = taskMaterial.TaskInstance;
            this.dataManager.deleteEntity(taskMaterial);
            if (!taskInstance.TaskMaterial.length) {
                this.workflowService.deleteTask(taskInstance);
            }
        }

        const taskCohortInputs = taskCohort.TaskCohortInput;
        for (const taskCohortInput of taskCohortInputs.slice()) {
            this.dataManager.deleteEntity(taskCohortInput);
        }
        this.dataManager.deleteEntity(taskCohort);
        

        if (this.featureFlagService.getIsCRO()) {
            const sampleGroupSourceMaterialLookup = createLookup(
                sampleGroupSourceMaterials, 
                "C_AnimalPlaceholder_key"
            );
            const jobCohort = cohort.JobCohort.find(jc => jc.C_Job_key === job.C_Job_key);
            for (const placeholder of jobCohort.Placeholder.slice()) {
                const taskPlaceholder = placeholder.TaskPlaceholder
                    .find(tp => tp.C_TaskInstance_key = taskCohort.C_TaskInstance_key);
                for (const animalPlaceholder of placeholder.AnimalPlaceholder.slice()) {
                    const sourceMaterialToDelete = sampleGroupSourceMaterialLookup
                        .get(animalPlaceholder.C_AnimalPlaceholder_key);
                    if (!sourceMaterialToDelete) {
                        continue;
                    }

                    this.dataManager.deleteEntity(sourceMaterialToDelete);
                }

                for (const taskPlaceholderInput of taskPlaceholder.TaskPlaceholderInput.slice()) {
                    this.dataManager.deleteEntity(taskPlaceholderInput);
                }

                this.dataManager.deleteEntity(taskPlaceholder);
            }
        }
    }
    
    private findMaterialsThatHaveSamplesCreatedFrom(taskCohort: TaskCohort) {
        const materialsWithSamples: Material[] = [];
        for (const cohortMaterial of taskCohort.Cohort.CohortMaterial) {
            const materialIsRelatedToSampleGroupWithSamples = this.jobPharmaDetailService
                .isMaterialRelatedToSampleGroupWithSamples(cohortMaterial.Material, taskCohort.TaskInstance);
            if (materialIsRelatedToSampleGroupWithSamples) {
                materialsWithSamples.push(cohortMaterial.Material);
            }
        }
        return materialsWithSamples;
    }

    private isTaskLocked(taskInstance: TaskInstance) {
        const isSampleGroupsEditableFlag = this.jobPharmaDetailService.isSampleGroupsEditableFlag();
        return (isSampleGroupsEditableFlag && isAnyTaskEndState(taskInstance.MemberTaskInstance)) ||
            !isSampleGroupsEditableFlag;
    }

    private findMaterialsWithData(taskCohort: TaskCohort, lookup: Map<number, TaskCohort[]>) {
        const memberTaskInstances = taskCohort.TaskInstance.MemberTaskInstance;
        const materialsWithData: Set<number> = new Set();
        for (const taskInstance of memberTaskInstances) {
            if (taskInstance?.cv_TaskStatus?.IsEndState) {
                for (const taskMaterial of taskInstance.TaskMaterial) {
                    const key = taskMaterial.C_Material_key;
                    if (!lookup.has(key) || lookup.get(key).length > 1) {
                        continue;
                    }
    
                    materialsWithData.add(taskMaterial.C_Material_key);
                }
            }


            for (const taskOutputSet of taskInstance.TaskOutputSet) {
                if (!taskOutputSet.CollectionDateTime || !taskOutputSet.TaskOutputSetMaterial) {
                    continue;
                }
                
                for (const taskOutputSetMaterial of taskOutputSet.TaskOutputSetMaterial) {
                    const key = taskOutputSetMaterial.C_Material_key;
                    if (!lookup.has(key) || lookup.get(key).length > 1) {
                        continue;
                    }

                    materialsWithData.add(taskOutputSetMaterial.C_Material_key);
                }
            }
        }

        return [...materialsWithData];
    }

    private createTaskCohortMaterialLookup(taskCohort: TaskCohort) {
        const cohortMaterials = new Set(taskCohort.Cohort.CohortMaterial.map(cm => cm.C_Material_key));
        const taskCohortMaterialLookup = taskCohort.TaskInstance.TaskCohort
            .reduce((acc: Map<number, TaskCohort[]>, cur) => {
                for (const cohortMaterial of cur.Cohort.CohortMaterial) {
                    if (!cohortMaterials.has(cohortMaterial.C_Material_key)) {
                        continue;
                    }
                    
                    const key = cohortMaterial.C_Material_key;
                    if (!acc.has(key)) {
                        acc.set(key, []);
                    }

                    const taskCohorts = acc.get(key);
                    taskCohorts.push(cur);
                }

                return acc;
            }, new Map<number, TaskCohort[]>());

        return taskCohortMaterialLookup;
    }
}
